import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { toast } from "sonner";
import { Check, Pencil, X, Send, SkipForward, SearchCheck, Linkedin, FileEdit, Database, Lock, ShieldAlert, RefreshCw, Mail } from "lucide-react";
import { EvidenceList } from "@/components/Evidence";

const OPP_STATUS = {
  draft_ready: "bg-amber-950/60 text-amber-300 border-amber-800/60",
  approved: "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  send_authorized: "bg-sky-950/60 text-sky-300 border-sky-800/60",
  sending: "bg-sky-950/60 text-sky-200 border-sky-700 animate-pulse",
  sent: "bg-indigo-950/60 text-indigo-300 border-indigo-800/60",
  sent_reconciliation_required: "bg-amber-950/60 text-amber-200 border-amber-700",
  not_sent: "bg-rose-950/60 text-rose-300 border-rose-800/60",
  failed: "bg-rose-950/60 text-rose-300 border-rose-800/60",
  blocked: "bg-rose-950/60 text-rose-300 border-rose-800/60",
  replied: "bg-pink-950/60 text-pink-300 border-pink-800/60",
  rejected: "bg-rose-950/60 text-rose-300 border-rose-800/60",
};

const RECEIPT_STATUS_STYLE = {
  persisted: "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  not_sent: "bg-rose-950/60 text-rose-300 border-rose-800/60",
  reconciliation_required: "bg-amber-950/60 text-amber-200 border-amber-700",
  duplicate_prevented: "bg-slate-800/60 text-slate-300 border-slate-600",
};

const RECEIPT_STATUS_LABEL = {
  persisted: "Sent",
  not_sent: "Not Sent",
  reconciliation_required: "Sent · Reconciliation Required",
  duplicate_prevented: "Duplicate Prevented",
};

const TIER_STYLES = {
  strong:   "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  moderate: "bg-amber-950/60 text-amber-300 border-amber-800/60",
  weak:     "bg-rose-950/60 text-rose-300 border-rose-800/60",
  not_evaluated: "bg-slate-800/60 text-slate-500 border-slate-700",
};

function ScoreBreakdown({ b }) {
  if (!b) return null;
  const rows = [
    ["Problem Visibility", b.problem_visibility],
    ["Reachability",       b.reachability],
    ["Buyer Capacity",     b.buyer_capacity],
    ["Business Consequence", b.business_consequence],
    ["Offer Fit",          b.offer_fit],
  ].filter(([, v]) => v != null);
  const total = b.total, min = b.minimum, ratio = b.ratio;
  return (
    <div className="border border-slate-800 bg-slate-950/40 rounded-md p-3 space-y-2" data-testid="score-breakdown-panel">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">KNKT per-Opportunity fit model</div>
        <span className="font-mono text-[9px] uppercase tracking-widest text-slate-600" title="Authoritative fields per KNKT production Airtable">authoritative</span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {rows.map(([label, v]) => (
          <div key={label} className="flex items-center justify-between text-[11px]">
            <span className="text-slate-400">{label}</span>
            <span className="font-mono text-slate-200">{v}</span>
          </div>
        ))}
        <div className="flex items-center justify-between text-sm border-t border-slate-800 pt-2 col-span-2">
          <span className="text-slate-100 font-medium">Total Score</span>
          <span className="font-mono text-emerald-300 text-base">
            {total ?? "—"}
            <span className="text-slate-500 text-xs ml-1">vs min {min ?? "—"}</span>
          </span>
        </div>
        {b.ai_delivery_percent != null && (
          <div className="flex items-center justify-between text-[11px] col-span-2">
            <span className="text-slate-500">AI delivery %</span>
            <span className="font-mono text-slate-300">{Math.round(b.ai_delivery_percent * 100)}%</span>
          </div>
        )}
        {b.lawn_chair_test && (
          <div className="flex items-center justify-between text-[11px] col-span-2">
            <span className="text-slate-500">Lawn-Chair Test</span>
            <span className={`font-mono ${b.lawn_chair_test === "Pass" ? "text-emerald-300" : "text-rose-300"}`}>{b.lawn_chair_test}</span>
          </div>
        )}
      </div>
      <div className="border-t border-slate-800 pt-2 text-[10px] font-mono text-slate-500 leading-relaxed" title="UI-derived interpretation">
        <span className="text-amber-400">⚠ UI-derived interpretation</span> — tier ({(ratio != null ? `${(ratio * 100).toFixed(0)}% of min` : "n/a")}) is a display-only categorization. Owner thresholds have not been intentionally defined; Total Score and Minimum Opportunity Score remain the authoritative production values.
      </div>
    </div>
  );
}

const LIFECYCLE_STYLES = {
  discovery: "bg-amber-950/60 text-amber-300 border-amber-800/60",
  proposal:  "bg-orange-950/60 text-orange-300 border-orange-800/60",
  won:       "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  lost:      "bg-rose-950/60 text-rose-300 border-rose-800/60",
};

const DECISION_BTN = {
  approve: "bg-[#064E3B] text-[#34D399] border-[#059669] hover:brightness-125",
  revise: "bg-[#78350F] text-[#FBBF24] border-[#D97706] hover:brightness-125",
  reject: "bg-[#7F1D1D] text-[#F87171] border-[#DC2626] hover:brightness-125",
  send: "bg-[#0C4A6E] text-[#38BDF8] border-[#0284C7] hover:brightness-125",
  pass: "bg-[#1E293B] text-[#94A3B8] border-[#475569] hover:bg-slate-700",
  investigate: "bg-[#312E81] text-[#A5B4FC] border-[#4F46E5] hover:brightness-125",
};

function DecisionButton({ action, label, icon: Icon, shortcut, style, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-all disabled:opacity-30 disabled:cursor-not-allowed ${style}`}
    >
      <Icon size={12} />
      {label}
      {shortcut && <kbd className="ml-1 px-1 rounded bg-black/30 font-mono text-[10px]">{shortcut}</kbd>}
    </button>
  );
}

export default function Opportunities() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("selected");
  const [revising, setRevising] = useState(false);
  const [revisedBody, setRevisedBody] = useState("");
  const [blockedClaims, setBlockedClaims] = useState(null);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const qc = useQueryClient();

  const { data: opportunities = [], isLoading } = useQuery({
    queryKey: ["opportunities"],
    queryFn: async () => (await api.get("/opportunities")).data,
  });
  const { data: detail } = useQuery({
    queryKey: ["opportunity", selectedId],
    queryFn: async () => (await api.get(`/opportunities/${selectedId}`)).data,
    enabled: !!selectedId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["opportunities"] });
    qc.invalidateQueries({ queryKey: ["opportunity", selectedId] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const decide = useMutation({
    mutationFn: async (payload) => (await api.post("/decisions", payload)).data,
    onSuccess: (d) => {
      const receipt = d.send_receipt;
      if (receipt) {
        const rs = receipt.receipt_status;
        if (rs === "persisted") toast.success(`Send persisted · ${receipt.provider_send_id}${receipt.mode === "dry_run" ? " (dry-run)" : ""}`);
        else if (rs === "reconciliation_required") toast.error("Sent but reconciliation required — resolve Airtable receipt manually");
        else if (rs === "not_sent") toast.error(`Not sent: ${receipt.reason || "gate failed"}`);
        else toast.error(`Send ${rs}: ${receipt.reason || "gate failed"}`);
      } else {
        toast.success(`Decision recorded: ${d.action}`);
      }
      setRevising(false); setBlockedClaims(null); setShowSendConfirm(false); invalidate();
    },
    onError: (e) => {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.blocked_claims) {
        setBlockedClaims(detail.blocked_claims);
        toast.error(detail.message || "Approval blocked — unsupported claims");
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Decision failed"));
      }
      setShowSendConfirm(false);
    },
  });

  const retrySend = useMutation({
    mutationFn: async (did) => (await api.post(`/drafts/${did}/retry_send`)).data,
    onSuccess: (r) => {
      const s = r.receipt.receipt_status;
      if (s === "duplicate_prevented") toast.success("Idempotency held: no duplicate send");
      else if (s === "persisted") toast.success(`Send persisted · ${r.receipt.provider_send_id}`);
      else if (s === "reconciliation_required") toast.error("Sent but reconciliation required — resolve manually");
      else toast.error(`Retry result: ${s}`);
      invalidate();
    },
    onError: (e) => {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Retry failed"));
    },
  });

  const draft = detail?.draft;
  const prospect = detail?.prospect;

  const act = (action, extra = {}) => {
    if (!detail) return;
    if (["approve_draft", "revise_draft", "reject_draft", "authorize_send"].includes(action)) {
      if (!draft) return;
      decide.mutate({ action, target_type: "draft", target_id: draft.id, ...extra });
    } else if (["pass_prospect", "investigate_prospect"].includes(action)) {
      decide.mutate({ action, target_type: "prospect", target_id: prospect.id, ...extra });
    } else if (action === "mark_linkedin_submitted") {
      decide.mutate({ action, target_type: "opportunity", target_id: detail.id, ...extra });
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (!detail || revising) return;
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const key = e.key.toLowerCase();
      const map = {
        a: () => draft?.status === "draft_ready" && act("approve_draft"),
        r: () => draft && openRevise(),
        j: () => draft && ["draft_ready", "approved"].includes(draft.status) && act("reject_draft"),
        s: () => draft?.status === "approved" && setShowSendConfirm(true),
        p: () => prospect && act("pass_prospect"),
        i: () => prospect && act("investigate_prospect"),
      };
      if (map[key]) { e.preventDefault(); map[key](); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const openRevise = () => {
    setRevisedBody(draft?.body || "");
    setRevising(true);
  };

  const evidenceMissing = draft && draft.claims?.some((c) => c.evidence_required) && !(draft.evidence_ids?.length);

  return (
    <div className="space-y-6" data-testid="opportunities-view">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100">Opportunities</h1>
        <p className="text-sm text-slate-400 mt-1">Proactive intelligence: prospect + contact + recommended outreach, with the decision surface.</p>
      </div>

      <div className="space-y-2" data-testid="opportunities-list">
          {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {opportunities.map((o) => (
            <div key={o.id}>
            <button
              onClick={() => setParams(selectedId === o.id ? {} : { selected: o.id })}
              data-testid={`opportunity-card-${o.id}`}
              className={`w-full text-left border rounded-lg p-4 space-y-2 transition-all duration-200 hover:border-slate-700 hover:shadow-lg hover:shadow-black/40 ${
                selectedId === o.id ? "border-sky-800/70 bg-slate-900 rounded-b-none" : "border-slate-800/80 bg-slate-900/60"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-base font-medium text-slate-200">{o.prospect?.name}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {o.fit_tier && o._source === "airtable" && (
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono border ${TIER_STYLES[o.fit_tier]}`} data-testid={`opportunity-fit-tier-${o.id}`}>
                      {o.fit_tier}
                    </span>
                  )}
                  <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-mono border ${OPP_STATUS[o.status] || OPP_STATUS.draft_ready}`} data-testid={`opportunity-status-${o.id}`}>
                    {o.status.replace("_", " ")}
                  </span>
                </div>
              </div>
              {o.primary_observation && (
                <div className="text-[11px] text-slate-400 leading-snug line-clamp-2">{o.primary_observation}</div>
              )}
              <div className="text-xs text-slate-400">{o.contact?.name} · {o.contact?.role}</div>
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-slate-500 flex-wrap">
                {o._source === "airtable" ? (
                  <span className="inline-flex items-center gap-1 text-emerald-300"><Database size={10} /> AIRTABLE</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-slate-500">MOCK</span>
                )}
                {o.source === "linkedin_marketplace" && <span className="inline-flex items-center gap-1 text-sky-400"><Linkedin size={10} /> marketplace</span>}
                <span>{o.channel}</span>
                {o.matched_offer && <span>· offer: {o.matched_offer}</span>}
                {o.draft && <span>· draft v{o.draft.version} · {o.draft.status.replace("_", " ")}</span>}
                {o.lifecycle_stage && (
                  <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono border ${LIFECYCLE_STYLES[o.lifecycle_stage]}`}
                        data-testid={`opportunity-lifecycle-${o.id}`}>
                    lifecycle: {o.lifecycle_stage}
                  </span>
                )}
              </div>
            </button>
            {selectedId === o.id && (
            <div className="border border-t-0 border-sky-800/70 bg-slate-900 rounded-b-lg p-4 space-y-4" data-testid="opportunity-detail-panel">
              {!detail && <p className="text-sm text-slate-500">Loading detail…</p>}
              {detail && (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-medium text-slate-200 tracking-tight">{prospect?.name}</h2>
                  <p className="text-xs text-slate-400">{detail.contact?.name} · {detail.contact?.role} · <span className="font-mono">{detail.contact?.email}</span></p>
                </div>
                <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-mono border ${OPP_STATUS[detail.status] || OPP_STATUS.draft_ready}`} data-testid="detail-opp-status">
                  {detail.status.replace("_", " ")}
                </span>
              </div>

              {draft && (
                <div className="border border-slate-800 rounded-md bg-slate-950/50 p-3 space-y-3" data-testid="draft-panel">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {draft.channel} draft · v{draft.version} · agent run {draft.agent_run_id?.slice(0, 8)}
                    </span>
                    <span className="font-mono text-[11px] text-slate-400" data-testid="draft-status">{draft.status.replace("_", " ")}</span>
                  </div>
                  {draft.subject && <div className="text-sm text-slate-200 font-medium">Subject: {draft.subject}</div>}
                  {revising ? (
                    <textarea
                      value={revisedBody}
                      onChange={(e) => setRevisedBody(e.target.value)}
                      rows={8}
                      data-testid="revise-body-input"
                      className="w-full bg-slate-900 border border-slate-700 rounded-md p-3 text-sm text-slate-200 font-mono focus:outline-none focus:ring-1 focus:ring-sky-500"
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm text-slate-300 leading-relaxed font-sans" data-testid="draft-body">{draft.body}</pre>
                  )}
                  <div className="space-y-1">
                    {draft.claims?.map((c, i) => {
                      const idx = c.index ?? i;
                      const blocked = blockedClaims?.find((b) => b.claim_index === idx);
                      const hasCitationForThis = (detail?.evidence || []).some((e) => e.claim_index === idx);
                      const supportState = !c.evidence_required
                        ? "not_required"
                        : blocked
                          ? "unsupported"
                          : hasCitationForThis
                            ? "supported"
                            : (draft.evidence_ids?.length ? "generic" : "unsupported");
                      const dot = {
                        supported: "bg-emerald-400",
                        generic: "bg-emerald-500/60",
                        unsupported: "bg-rose-400",
                        not_required: "bg-slate-600",
                      }[supportState];
                      return (
                        <div key={i} className="flex flex-col gap-0.5 text-[11px]" data-testid={`draft-claim-${idx}`}>
                          <div className="flex items-start gap-2">
                            <span className={`mt-0.5 inline-block w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
                            <span className="text-slate-400 flex-1">{c.text}</span>
                            {c.evidence_required && (
                              <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500 shrink-0">
                                {supportState === "supported" && "evidence: cited"}
                                {supportState === "generic" && "evidence: generic"}
                                {supportState === "unsupported" && "evidence: missing"}
                              </span>
                            )}
                          </div>
                          {blocked && (
                            <div className="ml-4 font-mono text-[10px] text-rose-300 leading-snug" data-testid={`blocked-claim-reason-${idx}`}>
                              ↳ {blocked.reason}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(evidenceMissing || blockedClaims?.length) && (
                    <div className="font-mono text-[11px] text-rose-300 bg-rose-950/40 border border-rose-800/50 rounded px-2 py-1.5" data-testid="evidence-block-warning">
                      {blockedClaims?.length
                        ? `Approval blocked: ${blockedClaims.length} factual claim(s) lack supporting evidence — see specific claim(s) above.`
                        : "Approval blocked: factual claims lack supporting evidence."}
                    </div>
                  )}
                  {draft.provider_message_id && (
                    <div className="font-mono text-[11px] text-slate-500">message-id: {draft.provider_message_id}</div>
                  )}
                  {draft.send_receipt && (
                    <div className={`border rounded-md p-3 space-y-1.5 ${
                      draft.send_receipt.receipt_status === "reconciliation_required"
                        ? "border-amber-700 bg-amber-950/20"
                        : draft.send_receipt.receipt_status === "not_sent"
                          ? "border-rose-800/60 bg-rose-950/20"
                          : "border-indigo-800/60 bg-indigo-950/30"
                    }`} data-testid="send-receipt-panel">
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-300 flex items-center gap-1.5">
                          <Mail size={11} /> Send Receipt · {draft.send_receipt.contract_version} · {draft.send_receipt.mode}
                        </div>
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono border ${RECEIPT_STATUS_STYLE[draft.send_receipt.receipt_status] || "bg-slate-800/60 text-slate-300 border-slate-600"}`} data-testid="send-receipt-status">
                          {RECEIPT_STATUS_LABEL[draft.send_receipt.receipt_status] || draft.send_receipt.receipt_status}
                          {draft.send_receipt.mode === "dry_run" ? " · DRY-RUN" : ""}
                        </span>
                      </div>
                      <div className="font-mono text-[11px] text-slate-300">provider send id: <span className="text-indigo-300" data-testid="send-receipt-provider-id">{draft.send_receipt.provider_send_id || "—"}</span></div>
                      {draft.send_receipt.sent_at && (
                        <div className="font-mono text-[10px] text-slate-500">sent at: <span className="text-slate-300" data-testid="send-receipt-sent-at">{draft.send_receipt.sent_at}</span></div>
                      )}
                      <div className="font-mono text-[10px] text-slate-500">receipt at: {draft.send_receipt.at}</div>
                      <div className="font-mono text-[10px] text-slate-500">recipient: <span className="text-slate-300">{draft.send_receipt.recipient || "—"}</span></div>
                      <div className="font-mono text-[10px] text-slate-500">idempotency key: <span className="text-slate-300 break-all">{draft.idempotency_key || "—"}</span></div>
                      {draft.send_receipt.receipt_status === "reconciliation_required" && (
                        <div className="border-l-2 border-amber-500 bg-amber-950/40 pl-3 py-2 text-[11px] text-amber-200 font-medium" data-testid="reconciliation-warning">
                          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-300 mb-1">⚠ Reconciliation required</div>
                          Email was dispatched but production receipt persistence failed. Retry/Send is prohibited from the app. Reconcile the Airtable receipt record manually before any further action.
                        </div>
                      )}
                      <details className="text-[10px] text-slate-500">
                        <summary className="cursor-pointer">validated gates ({draft.send_receipt.validated_gates?.length || 0})</summary>
                        <ul className="mt-1 space-y-0.5 pl-3 font-mono">
                          {(draft.send_receipt.validated_gates || []).map((g, i) => (
                            <li key={i} className={g.includes("FAIL") ? "text-rose-300" : "text-emerald-300"}>· {g}</li>
                          ))}
                        </ul>
                      </details>
                      {draft.send_receipt.reason && (
                        <div className="font-mono text-[11px] text-rose-300">reason: {draft.send_receipt.reason}</div>
                      )}
                      {draft.status === "sent" && draft.send_receipt.receipt_status === "persisted" && (
                        <button
                          onClick={() => retrySend.mutate(draft.id)}
                          disabled={retrySend.isPending}
                          data-testid="retry-send-btn"
                          className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium border bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700 transition-colors disabled:opacity-40"
                        >
                          <RefreshCw size={11} /> Retry (test idempotency)
                        </button>
                      )}
                      {draft.status === "not_sent" && (
                        <button
                          onClick={() => retrySend.mutate(draft.id)}
                          disabled={retrySend.isPending}
                          data-testid="retry-not-sent-btn"
                          className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-medium border bg-rose-950/60 text-rose-200 border-rose-700 hover:brightness-125 transition-all disabled:opacity-40"
                        >
                          <RefreshCw size={11} /> Retry send (after fixing underlying issue)
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Evidence & provenance ({detail.evidence?.length || 0})</div>
                <EvidenceList evidence={detail.evidence} />
              </div>

              <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-800/60" data-testid="decision-bar">
                {revising ? (
                  <>
                    <DecisionButton action="revise" label="Save Revision" icon={FileEdit} style={DECISION_BTN.revise} testid="decision-save-revision-btn"
                      onClick={() => act("revise_draft", { revised_body: revisedBody })} disabled={decide.isPending || !revisedBody.trim()} />
                    <DecisionButton action="pass" label="Cancel" icon={X} style={DECISION_BTN.pass} testid="decision-cancel-revision-btn" onClick={() => setRevising(false)} />
                  </>
                ) : (
                  <>
                    <DecisionButton action="approve" label="Approve" icon={Check} shortcut="A" style={DECISION_BTN.approve} testid="decision-approve-btn"
                      onClick={() => act("approve_draft")} disabled={decide.isPending || draft?.status !== "draft_ready"} />
                    <DecisionButton action="revise" label="Revise" icon={Pencil} shortcut="R" style={DECISION_BTN.revise} testid="decision-revise-btn"
                      onClick={openRevise} disabled={decide.isPending || !draft || draft.status === "sent"} />
                    <DecisionButton action="reject" label="Reject" icon={X} shortcut="J" style={DECISION_BTN.reject} testid="decision-reject-btn"
                      onClick={() => act("reject_draft")} disabled={decide.isPending || !draft || !["draft_ready", "approved"].includes(draft?.status)} />
                    <DecisionButton action="send" label="Authorize Send" icon={Send} shortcut="S" style={DECISION_BTN.send} testid="decision-authorize-send-btn"
                      onClick={() => setShowSendConfirm(true)} disabled={decide.isPending || draft?.status !== "approved"} />
                  </>
                )}
              </div>

              {detail.source === "linkedin_marketplace" && !detail.linkedin_submitted && (
                <button
                  onClick={() => act("mark_linkedin_submitted")}
                  data-testid="mark-linkedin-submitted-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-sky-950/60 text-sky-300 border-sky-800/60 hover:brightness-125 transition-all"
                >
                  <Linkedin size={12} /> Mark LinkedIn proposal as submitted (manual)
                </button>
              )}
              {detail.linkedin_submitted && (
                <div className="font-mono text-[11px] text-emerald-400" data-testid="linkedin-submitted-flag">
                  LinkedIn submission recorded · {new Date(detail.linkedin_submitted_at).toLocaleString()}
                </div>
              )}

              {showSendConfirm && draft && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="send-confirm-modal" onClick={() => setShowSendConfirm(false)}>
                  <div className="max-w-2xl w-full border border-sky-800/60 bg-slate-950 rounded-lg p-5 space-y-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-medium text-slate-100 flex items-center gap-2">
                          <ShieldAlert size={16} className="text-amber-400" /> Confirm Authorize Send
                        </h3>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mt-1">
                          Phase 2A · DRY-RUN mode · no external SMTP call
                        </p>
                      </div>
                      <button onClick={() => setShowSendConfirm(false)} className="text-slate-500 hover:text-slate-200" data-testid="send-confirm-cancel-x"><X size={16} /></button>
                    </div>
                    <div className="border-l-2 border-amber-800/60 pl-3 text-[11px] text-slate-300 leading-snug">
                      Authorizing send is a distinct decision from content approval. The send primitive will independently re-validate every gate and record an immutable receipt.
                    </div>
                    <div className="space-y-2 font-mono text-[11px]">
                      <div><span className="text-slate-500 uppercase tracking-widest text-[9px]">Recipient</span><div className="text-emerald-300 text-sm break-all" data-testid="send-confirm-recipient">{detail.contact?.email || "—"}</div></div>
                      <div><span className="text-slate-500 uppercase tracking-widest text-[9px]">Subject</span><div className="text-slate-200 text-sm" data-testid="send-confirm-subject">{draft.subject || "—"}</div></div>
                      <div><span className="text-slate-500 uppercase tracking-widest text-[9px]">Body (v{draft.version})</span>
                        <pre className="whitespace-pre-wrap text-sm text-slate-300 leading-relaxed font-sans border border-slate-800 rounded-md bg-slate-900/60 p-3 max-h-56 overflow-y-auto" data-testid="send-confirm-body">{draft.body}</pre>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-800/60">
                      <button
                        onClick={() => act("authorize_send")}
                        disabled={decide.isPending}
                        data-testid="send-confirm-authorize-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-[#0C4A6E] text-[#38BDF8] border-[#0284C7] hover:brightness-125 transition-all disabled:opacity-40"
                      >
                        <Send size={12} /> Authorize & Dispatch (dry-run)
                      </button>
                      <button
                        onClick={() => setShowSendConfirm(false)}
                        data-testid="send-confirm-cancel-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700 transition-colors"
                      >
                        <X size={12} /> Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
            </div>
            )}
            </div>
          ))}
        </div>
    </div>
  );
}
